import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { AlertTriangle, KeyRound, Loader2, ShieldCheck, X } from 'lucide-react'
import * as api from '../api/auth'
import { ApiError } from '../api/auth'
import {
  ROLE_KEYS,
  ROLE_LABELS,
  type AuthUser,
  type ManagedUser,
  type PermissionDefinition,
  type RoleKey,
} from '../types/identity'
import { entitledStores } from '../lib/access'

const RANK = new Map<RoleKey, number>(ROLE_KEYS.map((role, index) => [role, index]))
const COMPANY_WIDE = new Set<RoleKey>(['operations_manager'])

function roleMinimumReached(role: RoleKey, minimum: RoleKey): boolean {
  return (RANK.get(role) ?? -1) >= (RANK.get(minimum) ?? 99)
}

/**
 * Role, store-scope and per-user permission editor.
 *
 * The role is the baseline; grants add capabilities and revocations remove
 * them. The backend rejects a grant the editor does not hold and normalizes the
 * lists to actual differences, so this dialog can safely show the whole
 * catalogue as a set of checkboxes.
 */
export function AccessEditorDialog({
  target,
  actor,
  roles,
  stores,
  catalog,
  onClose,
  onSaved,
}: {
  target: ManagedUser
  actor: AuthUser
  roles: RoleKey[]
  stores: api.StoreOption[]
  catalog: PermissionDefinition[]
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [role, setRole] = useState<RoleKey>(target.role)
  const [storeId, setStoreId] = useState<number | null>(target.store_id)
  const [scopeIds, setScopeIds] = useState<number[]>(target.scope_store_ids ?? [])
  const [grants, setGrants] = useState<string[]>(target.permission_grants ?? [])
  const [revocations, setRevocations] = useState<string[]>(target.permission_revocations ?? [])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const allowedStores = useMemo(() => entitledStores(actor, stores), [actor, stores])
  const roleOptions = useMemo(
    () => (roles.includes(target.role) ? roles : [target.role, ...roles]),
    [roles, target.role],
  )
  const baseCapabilities = useMemo(
    () =>
      new Set(
        catalog
          .filter(
            (item) =>
              target.is_platform_admin || roleMinimumReached(role, item.min_role),
          )
          .map((item) => item.key),
      ),
    [catalog, role, target.is_platform_admin],
  )
  const effectiveCapabilities = useMemo(() => {
    const result = new Set(baseCapabilities)
    for (const key of grants) result.add(key)
    for (const key of revocations) result.delete(key)
    return result
  }, [baseCapabilities, grants, revocations])
  const actorCapabilities = useMemo(
    () => new Set(actor.capabilities ?? []),
    [actor.capabilities],
  )
  const groups = useMemo(() => {
    const grouped = new Map<string, PermissionDefinition[]>()
    for (const item of catalog) {
      // Audit/activity logs are a hard system-admin boundary, not an editable
      // per-user grant; Legacy keys are not part of the editor vocabulary.
      if (item.group === 'Legacy' || item.group === 'Audit') continue
      // The editor only offers permissions the signed-in person holds. Anything
      // else cannot be granted and is omitted rather than shown disabled.
      if (!actor.is_platform_admin && !actorCapabilities.has(item.key)) continue
      const entries = grouped.get(item.group) ?? []
      entries.push(item)
      grouped.set(item.group, entries)
    }
    return [...grouped.entries()]
  }, [catalog, actor.is_platform_admin, actorCapabilities])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  function changeRole(next: RoleKey) {
    setRole(next)
    if (COMPANY_WIDE.has(next)) {
      setStoreId(null)
      setScopeIds([])
      return
    }
    if (next === 'district_manager') {
      setStoreId(null)
      if (scopeIds.length === 0 && allowedStores[0]) setScopeIds([allowedStores[0].id])
      return
    }
    setScopeIds([])
    if (storeId === null && allowedStores[0]) setStoreId(allowedStores[0].id)
  }

  function togglePermission(key: string) {
    const isOn = effectiveCapabilities.has(key)
    const inherited = baseCapabilities.has(key)
    if (isOn) {
      setGrants((current) => current.filter((item) => item !== key))
      if (inherited) {
        setRevocations((current) => (current.includes(key) ? current : [...current, key]))
      }
      return
    }
    setRevocations((current) => current.filter((item) => item !== key))
    if (!inherited) {
      setGrants((current) => (current.includes(key) ? current : [...current, key]))
    }
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)
    const isCompanyWide = COMPANY_WIDE.has(role)
    const isDistrict = role === 'district_manager'
    if (isDistrict && scopeIds.length === 0) {
      setError('Choose at least one shop for a district manager.')
      setBusy(false)
      return
    }
    if (!isCompanyWide && !isDistrict && storeId === null) {
      setError('Choose the shop this person works in.')
      setBusy(false)
      return
    }
    void (async () => {
      try {
        await api.updateUserAccess(target.id, {
          role,
          store_id: isCompanyWide || isDistrict ? null : storeId,
          scope_store_ids: isDistrict ? scopeIds : null,
          permission_grants: grants,
          permission_revocations: revocations,
        })
        onSaved(`${target.full_name}'s role and access were updated.`)
        onClose()
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'That change could not be saved')
      } finally {
        setBusy(false)
      }
    })()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-slate-900/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Edit role and access for ${target.full_name}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <form
        onSubmit={submit}
        className="max-h-[92dvh] w-full max-w-2xl space-y-4 overflow-y-auto rounded-t-3xl border border-slate-200 bg-white p-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl sm:max-h-[85vh] sm:rounded-2xl sm:p-5 dark:border-white/10 dark:bg-ink-900"
      >
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-salsa-500/10 text-salsa-600 dark:text-salsa-300">
            <KeyRound className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="leading-tight">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
              Role and access
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {target.full_name} · {ROLE_LABELS[target.role] ?? target.role}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="icon-button ml-auto"
            title="Close"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-xs font-medium text-slate-600 dark:text-slate-300">
            Role
            <select
              value={role}
              onChange={(event) => changeRole(event.target.value as RoleKey)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:[color-scheme:dark]"
            >
              {roleOptions.map((option) => (
                <option
                  key={option}
                  value={option}
                  className="bg-white text-slate-900 dark:bg-ink-900 dark:text-slate-100"
                >
                  {ROLE_LABELS[option] ?? option}
                </option>
              ))}
            </select>
          </label>
          <div className="space-y-1 text-xs font-medium text-slate-600 dark:text-slate-300">
            Store scope
            {COMPANY_WIDE.has(role) ? (
              <p className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
                All shops — company-wide access
              </p>
            ) : role === 'district_manager' ? (
              <div className="max-h-32 space-y-0.5 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 dark:border-white/10 dark:bg-white/5">
                {allowedStores.map((store) => (
                  <label
                    key={store.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg px-1.5 py-1 text-xs hover:bg-slate-50 dark:hover:bg-white/5"
                  >
                    <input
                      type="checkbox"
                      checked={scopeIds.includes(store.id)}
                      onChange={(event) =>
                        setScopeIds((current) =>
                          event.target.checked
                            ? [...current, store.id].sort((a, b) => a - b)
                            : current.filter((id) => id !== store.id),
                        )
                      }
                    />
                    <span>{store.label}</span>
                  </label>
                ))}
                {allowedStores.length === 0 && (
                  <p className="text-slate-400">No shops in your scope.</p>
                )}
              </div>
            ) : (
              <select
                value={storeId ?? ''}
                onChange={(event) => setStoreId(Number(event.target.value))}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:[color-scheme:dark]"
              >
                {allowedStores.map((store) => (
                  <option
                    key={store.id}
                    value={store.id}
                    className="bg-white text-slate-900 dark:bg-ink-900 dark:text-slate-100"
                  >
                    {store.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-salsa-500" aria-hidden="true" />
          <span>
            The role supplies the baseline. Grants and revocations below apply to this account
            only. You cannot grant a permission you do not hold yourself. Every change is audited
            in the activity log; no e-mail is sent for role or permission changes.
          </span>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Permissions
            </h3>
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              {effectiveCapabilities.size} effective
            </span>
          </div>
          <div className="max-h-72 space-y-3 overflow-y-auto rounded-xl border border-slate-200 p-3 dark:border-white/10">
            {groups.map(([group, items]) => (
              <div key={group} className="space-y-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                  {group}
                </p>
                {items.map((item) => {
                  const inherited = baseCapabilities.has(item.key)
                  const enabled = effectiveCapabilities.has(item.key)
                  const canGrant =
                    inherited || actorCapabilities.has(item.key) || actor.is_platform_admin
                  const explicit =
                    grants.includes(item.key) || revocations.includes(item.key)
                  return (
                    <label
                      key={item.key}
                      className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-xs ${
                        canGrant
                          ? 'cursor-pointer hover:bg-slate-50 dark:hover:bg-white/5'
                          : 'cursor-not-allowed opacity-60'
                      }`}
                      title={
                        canGrant
                          ? item.description
                          : 'You do not hold this permission, so you cannot grant it.'
                      }
                    >
                      <input
                        type="checkbox"
                        checked={enabled}
                        disabled={busy || (!canGrant && !enabled)}
                        onChange={() => togglePermission(item.key)}
                        className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 accent-salsa-500"
                      />
                      <span className="min-w-0">
                        <span className="flex flex-wrap items-center gap-1.5 font-medium text-slate-700 dark:text-slate-200">
                          {item.label}
                          {inherited && (
                            <span className="badge bg-slate-500/10 text-[9px] text-slate-500 dark:text-slate-300">
                              role
                            </span>
                          )}
                          {explicit && (
                            <span className="badge bg-salsa-500/10 text-[9px] text-salsa-600 dark:text-salsa-300">
                              override
                            </span>
                          )}
                        </span>
                        <span className="block text-[10px] leading-snug text-slate-400 dark:text-slate-500">
                          {item.key} · minimum {ROLE_LABELS[item.min_role] ?? item.min_role}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-xl border border-salsa-200 bg-salsa-50/70 px-3 py-2 text-xs text-salsa-800 dark:border-salsa-500/25 dark:bg-salsa-500/10 dark:text-salsa-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4 dark:border-white/5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-white/5"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-salsa-600 disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Save role and access
          </button>
        </div>
      </form>
    </div>
  )
}
